import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { Typography } from '../typography/typography';
import { Icon, type IconName } from '../icon/icon';
import {
    toastRegion,
    toastStack,
    toastQueue,
    toastGhost,
    toastCard,
    toastIconFrame,
    toastIconTone,
    toastBody,
    toastActions,
    toastActionButton,
    toastActionPrimary,
    toastClose,
} from './toast.css';

export type ToastTone = 'success' | 'critical' | 'neutral';

export interface ToastAction {
    label: string;
    onPress: () => void | Promise<void>;
}

export interface ToastOptions {
    /** Bold lead line (e.g. "Stage updated"). */
    title: string;
    /** Optional supporting line (e.g. a `FEATURE_*` code explanation). */
    description?: string;
    tone?: ToastTone;
    /** Auto-dismiss delay in ms (default 5000). `0` keeps it until dismissed. */
    durationMs?: number;
    /**
     * Optional inline actions rendered under the text (e.g. Undo / View).
     * The last action is styled as the primary one; pressing any action
     * dismisses the toast.
     */
    actions?: readonly ToastAction[];
}

interface ToastEntry extends Required<Omit<ToastOptions, 'description' | 'actions'>> {
    id: number;
    description?: string;
    actions?: readonly ToastAction[];
}

export interface ToastApi {
    /** Show a toast with an explicit tone. Returns nothing. */
    show: (options: ToastOptions) => void;
    /** Convenience: a success toast (green featured icon). */
    success: (title: string, description?: string) => void;
    /** Convenience: a critical toast (red featured icon) — used for surfaced error codes. */
    error: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_DURATION = 5000;

const TONE_ICON: Record<ToastTone, IconName> = {
    success: 'check-circle',
    critical: 'alert-circle',
    neutral: 'info-circle',
};

/** At most this many ghost layers peek out above the front card. */
const MAX_GHOST_LAYERS = 2;

/**
 * Hosts the toast region and exposes the imperative `useToast` API. Toasts
 * anchor bottom-right as a card stack (newest in front, older ones peeking
 * behind, Untitled UI notification style), auto-dismiss, and are announced to
 * assistive tech (`alert` for critical, `status` for the rest).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<readonly ToastEntry[]>([]);
    // Monotonic id source — avoids Math.random / Date.now for stable keys.
    const nextId = useRef(0);
    const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const dismiss = useCallback((id: number) => {
        setToasts((current) => current.filter((t) => t.id !== id));
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);

    const show = useCallback(
        (options: ToastOptions) => {
            const id = nextId.current++;
            const entry: ToastEntry = {
                id,
                title: options.title,
                description: options.description,
                tone: options.tone ?? 'neutral',
                durationMs: options.durationMs ?? DEFAULT_DURATION,
                actions: options.actions,
            };
            setToasts((current) => [...current, entry]);
            if (entry.durationMs > 0) {
                timers.current.set(
                    id,
                    setTimeout(() => dismiss(id), entry.durationMs),
                );
            }
        },
        [dismiss],
    );

    // Clear any pending timers if the provider unmounts.
    useEffect(() => {
        const map = timers.current;
        return () => {
            map.forEach(clearTimeout);
            map.clear();
        };
    }, []);

    const api = useMemo<ToastApi>(
        () => ({
            show,
            success: (title, description) => show({ title, description, tone: 'success' }),
            error: (title, description) => show({ title, description, tone: 'critical' }),
        }),
        [show],
    );

    // Newest toast is the front card; older ones wait behind as ghost layers.
    const front = toasts[toasts.length - 1];
    const queued = toasts.slice(0, -1);
    const ghostLayers = Math.min(queued.length, MAX_GHOST_LAYERS);

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div className={toastRegion} aria-live="polite">
                {queued.length > 0 ? (
                    <div className={toastQueue}>
                        {queued.map((toast) => (
                            <div key={toast.id} role={toast.tone === 'critical' ? 'alert' : 'status'}>
                                {toast.title}
                                {toast.description ? ` — ${toast.description}` : ''}
                            </div>
                        ))}
                    </div>
                ) : null}
                {front ? (
                    <div className={toastStack}>
                        {Array.from({ length: ghostLayers }, (_, i) => ghostLayers - i).map((depth) => (
                            <div
                                key={depth}
                                className={toastGhost}
                                aria-hidden
                                data-testid="toast-ghost"
                                style={{ insetInline: depth * 8, insetBlockStart: depth * -9 }}
                            />
                        ))}
                        <div
                            key={front.id}
                            className={toastCard}
                            role={front.tone === 'critical' ? 'alert' : 'status'}
                        >
                            <span className={`${toastIconFrame} ${toastIconTone[front.tone]}`} aria-hidden>
                                <Icon name={TONE_ICON[front.tone]} />
                            </span>
                            <div className={toastBody}>
                                <Typography variant="body-md" fontWeight="semibold">
                                    {front.title}
                                </Typography>
                                {front.description ? (
                                    <Typography variant="body-sm" tone="text-secondary">
                                        {front.description}
                                    </Typography>
                                ) : null}
                                {front.actions?.length ? (
                                    <div className={toastActions}>
                                        {front.actions.map((action, actionIndex) => (
                                            <button
                                                key={action.label}
                                                type="button"
                                                className={
                                                    actionIndex === (front.actions?.length ?? 0) - 1
                                                        ? `${toastActionButton} ${toastActionPrimary}`
                                                        : toastActionButton
                                                }
                                                onClick={() => {
                                                    void action.onPress();
                                                    dismiss(front.id);
                                                }}
                                            >
                                                {action.label}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                            <button
                                type="button"
                                className={toastClose}
                                aria-label="Dismiss notification"
                                onClick={() => dismiss(front.id)}
                            >
                                <Icon name="x" size={18} />
                            </button>
                        </div>
                    </div>
                ) : null}
            </div>
        </ToastContext.Provider>
    );
}

/** Access the toast API. Throws if used outside a `ToastProvider`. */
export function useToast(): ToastApi {
    const api = useContext(ToastContext);
    if (!api) throw new Error('useToast must be used within a ToastProvider');
    return api;
}
